import React, { useState, useRef, useEffect } from 'react';
import { Play, FastForward, Maximize } from 'lucide-react';
import './SplashScreen.css';

const VIDEO_URL = '/video/intro-fury-rose.mp4';
const STORAGE_KEY = 'fury-rose-visited';

const SplashScreen = ({ onComplete }) => {
  const [phase, setPhase] = useState('intro'); // 'intro' | 'playing'
  const [isFirstVisit, setIsFirstVisit] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    setIsFirstVisit(!localStorage.getItem(STORAGE_KEY));
  }, []);

  // Prevent body scroll while splash is visible
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const finish = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onComplete();
  };

  const handleStart = () => {
    setPhase('playing');
  };

  // When entering playing phase, force play (autoplay with sound is allowed here
  // because it's triggered by a user click).
  useEffect(() => {
    if (phase === 'playing' && videoRef.current) {
      videoRef.current.muted = false;
      const p = videoRef.current.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  }, [phase]);

  // On first visit, prevent the user from pausing the video
  const handlePause = () => {
    if (isFirstVisit && videoRef.current) {
      // Resume immediately if user managed to pause
      videoRef.current.play().catch(() => {});
    }
  };

  const requestFullscreen = () => {
    const v = videoRef.current;
    if (!v) return;
    // iOS Safari (webkit) — use the native video fullscreen
    if (typeof v.webkitEnterFullscreen === 'function') {
      try { v.webkitEnterFullscreen(); return; } catch (e) {}
    }
    // Standard Fullscreen API (Android Chrome, desktop)
    const target = v.requestFullscreen ? v : (v.parentElement || document.documentElement);
    const fn = target.requestFullscreen || target.webkitRequestFullscreen || target.msRequestFullscreen;
    if (fn) {
      try { fn.call(target); } catch (e) {}
    }
  };

  if (phase === 'intro') {
    return (
      <div
        className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
        data-testid="splash-intro"
      >
        <button
          onClick={handleStart}
          data-testid="splash-discover-btn"
          className="group flex items-center gap-4 px-8 md:px-12 py-5 md:py-6 border-2 border-white/40 rounded-full hover:border-white text-white text-lg md:text-2xl tracking-[0.2em] uppercase font-semibold transition-all duration-300 hover:scale-105 hover:bg-white/5"
        >
          <Play className="w-6 h-6 fill-current group-hover:scale-110 transition-transform" />
          <span>Découvrir le thème</span>
        </button>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
      data-testid="splash-playing"
    >
      <video
        ref={videoRef}
        src={VIDEO_URL}
        onEnded={finish}
        onPause={handlePause}
        onError={() => {
          // If video fails to load (CDN issue, slow network), skip the splash gracefully
          console.warn('Splash video failed to load — skipping');
          finish();
        }}
        autoPlay
        playsInline
        preload="auto"
        controls={false}
        className="w-full h-full object-contain splash-video-rotate"
        data-testid="splash-video"
      />
      {/* Fullscreen button (visible on mobile) */}
      <button
        onClick={requestFullscreen}
        data-testid="splash-fullscreen-btn"
        className="splash-fullscreen-btn absolute top-6 left-6 z-10 items-center gap-2 px-3 py-2 bg-white/10 backdrop-blur-md border border-white/30 rounded-full text-white text-sm font-semibold hover:bg-white/20 transition-colors"
      >
        <Maximize className="w-4 h-4" />
        <span>Plein écran</span>
      </button>
      {!isFirstVisit && (
        <button
          onClick={finish}
          data-testid="splash-skip-btn"
          className="absolute top-6 right-6 z-10 flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/30 rounded-full text-white text-sm font-semibold hover:bg-white/20 transition-colors"
        >
          <FastForward className="w-4 h-4" />
          Passer
        </button>
      )}
    </div>
  );
};

export default SplashScreen;
